// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { httpsCallable } from 'firebase/functions';
import { deleteDoc, deleteField, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { functions, db } from '../../utils/firebase';
import { CRAWL_SCHEDULES_COLLECTION } from '../../utils/firestoreCollections';
import useCrawlSchedules, {
  CURSOR_ERROR_CODE,
} from '../../hooks/useCrawlSchedules';
import CreateScheduleDialog from './CreateScheduleDialog';
import { STATUSES, formatScheduleStatusLabel } from './scheduleFormConstants';

const cadenceColumnSx = {
  width: '8%',
  maxWidth: 88,
  whiteSpace: 'nowrap',
};

const SCHEDULE_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  ...STATUSES.map(function scheduleStatusFilterOption(statusValue) {
    return { value: statusValue, label: statusValue };
  }),
];

function formatTs(d) {
  if (!d) return '—';
  return d instanceof Date ? d.toLocaleString() : String(d);
}

/** Maps crawl schedule targetType to admin detail route; extend when new types ship. */
const TARGET_TYPE_DETAIL_PATH = {
  brewery: (targetId) => `/brewery/${encodeURIComponent(targetId)}`,
};

function targetDetailPath(targetType, targetId) {
  if (!targetId) {
    return null;
  }
  const build = targetType ? TARGET_TYPE_DETAIL_PATH[targetType] : null;
  return build ? build(targetId) : null;
}

function ScheduleErrorAlert({ scheduleId, message, onCloseError }) {
  const handleClose = useCallback(() => {
    onCloseError(scheduleId);
  }, [scheduleId, onCloseError]);
  return (
    <Alert severity="error" sx={{ mb: 2 }} onClose={handleClose}>
      {scheduleId}: {message}
    </Alert>
  );
}

function ScheduleSuccessAlert({ scheduleId, onCloseSuccess }) {
  const handleClose = useCallback(() => {
    onCloseSuccess(scheduleId);
  }, [scheduleId, onCloseSuccess]);
  return (
    <Alert severity="success" sx={{ mb: 2 }} onClose={handleClose}>
      Started crawl for schedule {scheduleId}
    </Alert>
  );
}

function SchedulesAlerts({ errorById, successById, onCloseError, onCloseSuccess }) {
  const errorEntries = Object.entries(errorById);
  const successEntries = Object.entries(successById);
  return (
    <>
      {errorEntries.map(function scheduleErrorEntry(entry) {
        const scheduleId = entry[0];
        const message = entry[1];
        return (
          <ScheduleErrorAlert
            key={`err-${scheduleId}`}
            scheduleId={scheduleId}
            message={message}
            onCloseError={onCloseError}
          />
        );
      })}
      {successEntries.map(function scheduleSuccessEntry(entry) {
        const scheduleId = entry[0];
        return (
          <ScheduleSuccessAlert
            key={`ok-${scheduleId}`}
            scheduleId={scheduleId}
            onCloseSuccess={onCloseSuccess}
          />
        );
      })}
    </>
  );
}

function ScheduleRowActions({
  isRowBusy,
  showExecuteSpinner,
  onEditClick,
  onDeleteClick,
  onExecuteClick,
  showPauseSchedule,
  showResumeSchedule,
  onPauseScheduleClick,
  onResumeScheduleClick,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        flexWrap: 'wrap',
      }}
    >
      <Tooltip title="Pause schedule dispatches">
        <span>
          <IconButton
            size="small"
            aria-label="Pause schedule"
            onClick={onPauseScheduleClick}
            disabled={isRowBusy || !showPauseSchedule}
          >
            <PauseCircleOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Resume paused schedule">
        <span>
          <IconButton
            size="small"
            aria-label="Resume schedule"
            onClick={onResumeScheduleClick}
            disabled={isRowBusy || !showResumeSchedule}
          >
            <PlayCircleOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Edit schedule">
        <span>
          <IconButton
            size="small"
            aria-label="Edit schedule"
            onClick={onEditClick}
            disabled={isRowBusy}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete schedule">
        <span>
          <IconButton
            size="small"
            color="error"
            aria-label="Delete schedule"
            onClick={onDeleteClick}
            disabled={isRowBusy}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={showExecuteSpinner ? 'Running…' : 'Execute crawl'}>
        <span>
          <IconButton
            size="small"
            color="primary"
            aria-label="Execute crawl"
            onClick={onExecuteClick}
            disabled={isRowBusy}
          >
            {showExecuteSpinner ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <PlayCircleIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function ScheduleTableRow({
  row,
  executingById,
  statusUpdatingById,
  onExecute,
  onEdit,
  onDelete,
  onPauseResume,
}) {
  const isExecuting = Boolean(executingById[row.id]);
  const isStatusUpdating = Boolean(statusUpdatingById[row.id]);
  const isRowBusy = isExecuting || isStatusUpdating;
  const targetLinkTo = targetDetailPath(row.targetType, row.targetId);

  const handleExecuteClick = useCallback(() => {
    onExecute(row.id);
  }, [row.id, onExecute]);

  const handleEditClick = useCallback(() => {
    onEdit(row);
  }, [row, onEdit]);

  const handleDeleteClick = useCallback(() => {
    onDelete(row);
  }, [row, onDelete]);

  const handlePauseScheduleClick = useCallback(() => {
    onPauseResume(row, 'paused');
  }, [row, onPauseResume]);

  const handleResumeScheduleClick = useCallback(() => {
    onPauseResume(row, 'active');
  }, [row, onPauseResume]);

  return (
    <TableRow>
      <TableCell>{row.crawlType}</TableCell>
      <TableCell>
        {targetLinkTo ? (
          <Link to={targetLinkTo}>{row.targetId}</Link>
        ) : (
          row.targetId
        )}
      </TableCell>
      <TableCell sx={cadenceColumnSx}>{row.cadence}</TableCell>
      <TableCell>
        {formatScheduleStatusLabel(row.status, row.pausedReason, row.lastErrorMessage)}
      </TableCell>
      <TableCell>{formatTs(row.nextDueAt)}</TableCell>
      <TableCell align="right">
        <ScheduleRowActions
          isRowBusy={isRowBusy}
          showExecuteSpinner={isExecuting}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onExecuteClick={handleExecuteClick}
          showPauseSchedule={row.status === 'active'}
          showResumeSchedule={row.status === 'paused'}
          onPauseScheduleClick={handlePauseScheduleClick}
          onResumeScheduleClick={handleResumeScheduleClick}
        />
      </TableCell>
    </TableRow>
  );
}

function SchedulesTableHeader() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Crawl type</TableCell>
        <TableCell>Target</TableCell>
        <TableCell sx={cadenceColumnSx}>Cadence</TableCell>
        <TableCell>Status</TableCell>
        <TableCell>Next due</TableCell>
        <TableCell align="right">Actions</TableCell>
      </TableRow>
    </TableHead>
  );
}

function SchedulesTableBody({
  schedules,
  loading,
  executingById,
  statusUpdatingById,
  onExecute,
  onEdit,
  onDelete,
  onPauseResume,
  error,
  isCursorError,
  hasActiveFilters,
}) {
  if (loading) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
            <CircularProgress size={32} />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }
  if (error && !isCursorError) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={6}>
            <Typography variant="body2" color="error">
              Failed to load schedules: {error.message}
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }
  if (isCursorError) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={6}>
            <Typography variant="body2" color="text.secondary">
              Cannot show this page until pagination is reset. Use the warning above or return to
              the first page.
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }
  if (schedules.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={6}>
            <Typography variant="body2" color="text.secondary">
              {hasActiveFilters
                ? 'No schedules match these filters.'
                : 'No schedules on this page.'}
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }
  return (
    <TableBody>
      {schedules.map(function scheduleDataRow(schRow) {
        return (
          <ScheduleTableRow
            key={schRow.id}
            row={schRow}
            executingById={executingById}
            statusUpdatingById={statusUpdatingById}
            onExecute={onExecute}
            onEdit={onEdit}
            onDelete={onDelete}
            onPauseResume={onPauseResume}
          />
        );
      })}
    </TableBody>
  );
}

function SchedulesDataTable({
  schedules,
  loading,
  executingById,
  statusUpdatingById,
  onExecute,
  onEdit,
  onDelete,
  onPauseResume,
  error,
  isCursorError,
  hasActiveFilters,
}) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
      <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
        <SchedulesTableHeader />
        <SchedulesTableBody
          schedules={schedules}
          loading={loading}
          executingById={executingById}
          statusUpdatingById={statusUpdatingById}
          onExecute={onExecute}
          onEdit={onEdit}
          onDelete={onDelete}
          onPauseResume={onPauseResume}
          error={error}
          isCursorError={isCursorError}
          hasActiveFilters={hasActiveFilters}
        />
      </Table>
    </TableContainer>
  );
}

function SchedulesFiltersBar({
  statusFilter,
  targetInput,
  onStatusChange,
  onTargetInputChange,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        mb: 2,
      }}
    >
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="sched-status-filter-label">Status</InputLabel>
        <Select
          labelId="sched-status-filter-label"
          label="Status"
          value={statusFilter}
          onChange={onStatusChange}
        >
          {SCHEDULE_STATUS_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value || 'all'} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        label="Target contains"
        placeholder="Target ID substring"
        value={targetInput}
        onChange={onTargetInputChange}
        sx={{ minWidth: 240, flex: '1 1 200px' }}
      />
    </Box>
  );
}

function DeleteScheduleConfirmDialog({
  open,
  scheduleId,
  onCancel,
  onConfirm,
  deleting,
}) {
  return (
    <Dialog open={open} onClose={deleting ? undefined : onCancel}>
      <DialogTitle>Delete schedule?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          This removes schedule <code>{scheduleId}</code> from Firestore. Executions history is not
          deleted. This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={deleting}>
          Cancel
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained" disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

function SchedulesPaginationBar({
  page,
  rowsPerPage,
  hasNextPage,
  loading,
  onPrevPage,
  onNextPage,
  onRowsPerPageChange,
}) {
  const handleRowsChange = useCallback(
    (event) => {
      onRowsPerPageChange(Number(event.target.value));
    },
    [onRowsPerPageChange],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 2,
        mt: 2,
      }}
    >
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id="schedules-rows-label">Rows per page</InputLabel>
        <Select
          labelId="schedules-rows-label"
          label="Rows per page"
          value={rowsPerPage}
          onChange={handleRowsChange}
        >
          {ROWS_PER_PAGE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="body2" color="text.secondary">
        Page {page + 1}
        {!hasNextPage && !loading ? ' · End' : ''}
      </Typography>
      <Button size="small" variant="outlined" disabled={page === 0 || loading} onClick={onPrevPage}>
        Previous
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={!hasNextPage || loading}
        onClick={onNextPage}
      >
        Next
      </Button>
    </Box>
  );
}

function SchedulesTab() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');
  const [targetInput, setTargetInput] = useState('');
  const [targetFilter, setTargetFilter] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(0);
      setTargetFilter(targetInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [targetInput]);

  const { schedules, loading, error, hasNextPage, refetchSchedules, scanResultTruncated } =
    useCrawlSchedules({
      page,
      rowsPerPage,
      statusFilter,
      targetFilter,
    });

  const hasActiveFilters = Boolean(statusFilter) || Boolean(targetFilter);

  const handleStatusFilterChange = useCallback((event) => {
    setPage(0);
    setStatusFilter(event.target.value);
  }, []);

  const handleSchedulesTargetTextChange = useCallback(
    function handleSchedulesTargetTextChange(event) {
      setTargetInput(event.target.value);
    },
    [],
  );

  const executeCrawlTask = useMemo(
    () =>
      httpsCallable(
        functions,
        'callableSchedulerExecuteCrawlTask-executeCrawlTask',
      ),
    [],
  );
  const [executingById, setExecutingById] = useState({});
  const [statusUpdatingById, setStatusUpdatingById] = useState({});
  const [errorById, setErrorById] = useState({});
  const [successById, setSuccessById] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [listMutationError, setListMutationError] = useState(null);
  const successTimersByIdRef = useRef({});
  const isMountedRef = useRef(true);

  const handleDismissListMutationError = useCallback(function handleDismissListMutationError() {
    setListMutationError(null);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      Object.values(successTimersByIdRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      successTimersByIdRef.current = {};
    };
  }, []);

  const clearErrorForId = useCallback((scheduleId) => {
    setErrorById((prev) => {
      if (!(scheduleId in prev)) return prev;
      const next = { ...prev };
      delete next[scheduleId];
      return next;
    });
  }, []);

  const clearSuccessForId = useCallback((scheduleId) => {
    const tid = successTimersByIdRef.current[scheduleId];
    if (tid) {
      clearTimeout(tid);
      delete successTimersByIdRef.current[scheduleId];
    }
    setSuccessById((previousSuccess) => {
      if (!(scheduleId in previousSuccess)) return previousSuccess;
      const nextSuccess = { ...previousSuccess };
      delete nextSuccess[scheduleId];
      return nextSuccess;
    });
  }, []);

  const handleSchedulePauseResume = useCallback(
    async function handleSchedulePauseResumeCallback(row, targetStatus) {
      if (!row?.id || (targetStatus !== 'paused' && targetStatus !== 'active')) {
        return;
      }
      if (row.status === targetStatus) {
        return;
      }
      setListMutationError(null);
      setStatusUpdatingById((previous) => ({ ...previous, [row.id]: true }));
      try {
        const updates = {
          status: targetStatus,
          updatedAt: serverTimestamp(),
        };
        if (targetStatus === 'active') {
          updates.pausedReason = deleteField();
        }
        await updateDoc(doc(db, CRAWL_SCHEDULES_COLLECTION, row.id), updates);
        if (!isMountedRef.current) {
          return;
        }
        refetchSchedules();
      } catch (mutationError) {
        console.error(mutationError);
        Sentry.captureException(mutationError);
        if (isMountedRef.current) {
          setListMutationError(mutationError?.message || 'Failed to update schedule status.');
        }
      } finally {
        if (isMountedRef.current) {
          setStatusUpdatingById((previous) => {
            if (!(row.id in previous)) {
              return previous;
            }
            const nextMap = { ...previous };
            delete nextMap[row.id];
            return nextMap;
          });
        }
      }
    },
    [refetchSchedules],
  );

  const runExecute = useCallback(
    async (scheduleId) => {
      setErrorById((prev) => {
        if (!(scheduleId in prev)) return prev;
        const next = { ...prev };
        delete next[scheduleId];
        return next;
      });
      setSuccessById((prev) => {
        if (!(scheduleId in prev)) return prev;
        const next = { ...prev };
        delete next[scheduleId];
        return next;
      });
      const prevTimer = successTimersByIdRef.current[scheduleId];
      if (prevTimer) {
        clearTimeout(prevTimer);
        delete successTimersByIdRef.current[scheduleId];
      }

      setExecutingById((prev) => ({ ...prev, [scheduleId]: true }));
      try {
        await executeCrawlTask({ scheduleId });
        if (!isMountedRef.current) {
          return;
        }
        setSuccessById((prev) => ({ ...prev, [scheduleId]: true }));
        successTimersByIdRef.current[scheduleId] = setTimeout(() => {
          if (!isMountedRef.current) {
            delete successTimersByIdRef.current[scheduleId];
            return;
          }
          setSuccessById((prev) => {
            if (!(scheduleId in prev)) return prev;
            const next = { ...prev };
            delete next[scheduleId];
            return next;
          });
          delete successTimersByIdRef.current[scheduleId];
        }, 4000);
      } catch (err) {
        if (isMountedRef.current) {
          console.error(err);
          Sentry.captureException(err);
          setErrorById((prev) => ({
            ...prev,
            [scheduleId]: err?.message || 'Execute failed',
          }));
        }
      } finally {
        if (isMountedRef.current) {
          setExecutingById((prev) => {
            if (!(scheduleId in prev)) return prev;
            const next = { ...prev };
            delete next[scheduleId];
            return next;
          });
        }
      }
    },
    [executeCrawlTask],
  );

  const handleRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
  }, []);

  const handlePrevPage = useCallback(() => {
    setPage((previous) => Math.max(0, previous - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((previous) => previous + 1);
  }, []);

  const handleResetPagination = useCallback(() => {
    setPage(0);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditTarget(null);
  }, []);

  const handleEditSchedule = useCallback((row) => {
    setListMutationError(null);
    setEditTarget(row);
  }, []);

  const handleDeleteRequest = useCallback((row) => {
    setListMutationError(null);
    setDeleteTarget(row);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    if (deleteSubmitting) {
      return;
    }
    setDeleteTarget(null);
  }, [deleteSubmitting]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleteSubmitting(true);
    setListMutationError(null);
    try {
      await deleteDoc(doc(db, CRAWL_SCHEDULES_COLLECTION, deleteTarget.id));
      if (isMountedRef.current) {
        setDeleteTarget(null);
        refetchSchedules();
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      if (isMountedRef.current) {
        setListMutationError(err?.message || 'Failed to delete schedule.');
      }
    } finally {
      if (isMountedRef.current) {
        setDeleteSubmitting(false);
      }
    }
  }, [deleteTarget, refetchSchedules]);

  const isCursorError = Boolean(error) && error.code === CURSOR_ERROR_CODE;

  const mutationErrorAlert =
    listMutationError ? (
      <Alert severity="error" sx={{ mb: 2 }} onClose={handleDismissListMutationError}>
        {listMutationError}
      </Alert>
    ) : null;

  const scanTruncatedAlert =
    scanResultTruncated && targetFilter.trim() ? (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Target search stopped at the scan limit before filling this page. Results may be incomplete;
        refine the target filter or try a smaller page size.
      </Alert>
    ) : null;

  return (
    <Box>
      <SchedulesFiltersBar
        statusFilter={statusFilter}
        targetInput={targetInput}
        onStatusChange={handleStatusFilterChange}
        onTargetInputChange={handleSchedulesTargetTextChange}
      />
      {scanTruncatedAlert}
      {mutationErrorAlert}
      <SchedulesAlerts
        errorById={errorById}
        successById={successById}
        onCloseError={clearErrorForId}
        onCloseSuccess={clearSuccessForId}
      />
      {isCursorError ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleResetPagination}>
              First page
            </Button>
          }
        >
          {error.message}
        </Alert>
      ) : null}
      <SchedulesDataTable
        schedules={schedules}
        loading={loading}
        executingById={executingById}
        statusUpdatingById={statusUpdatingById}
        onPauseResume={handleSchedulePauseResume}
        onExecute={runExecute}
        onEdit={handleEditSchedule}
        onDelete={handleDeleteRequest}
        error={error}
        isCursorError={isCursorError}
        hasActiveFilters={hasActiveFilters}
      />
      <SchedulesPaginationBar
        page={page}
        rowsPerPage={rowsPerPage}
        hasNextPage={hasNextPage}
        loading={loading}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
      <CreateScheduleDialog
        open={editTarget != null}
        onClose={handleCloseEdit}
        editSchedule={editTarget}
        onSaveSuccess={refetchSchedules}
      />
      <DeleteScheduleConfirmDialog
        open={deleteTarget != null}
        scheduleId={deleteTarget?.id ?? ''}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        deleting={deleteSubmitting}
      />
    </Box>
  );
}

export default SchedulesTab;
